// screens/RadarTopic.tsx — R3b (plate 02): one topic, whole. On spread.css
// like the other spreads (RadarProposal, AccountSpread, …) — hero = header
// + freshness + the item table grouped by verdict; aside = sources by
// state + earlier days.
//
// R3b-2 (2026-09-27): the in-place "Edit topic" form now sends cadence +
// notify through the same PUT the spec asks for, and its receipt is
// next_due_at, read back from the response — never a client-guessed time.
// The freshness line is the shared lib/radarFreshness formula (adds the
// `clusters` count the runs now carry).
//
// ⚑ "recall line = not measured" (the spec's own words) is not a filler
// string — RadarSourceApi carries no recall-rate field at all (verified
// against pythia-eden's fleet/radar/store.ts SourceRow), so every source's
// recall line reads exactly that until R0 stores the number.
import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  addToRadarNewsletter,
  getRadarTopic,
  listRadarItems,
  listRadarProposals,
  listRadarSourceCandidates,
  markRadarTopicViewed,
  raiseRadarItem,
  researchRadarTopicNow,
  setRadarSourceState,
  updateRadarTopic,
  type RadarItemApi,
  type RadarProposalApi,
  type RadarLastRunApi,
  type RadarRunApi,
  type RadarSourceApi,
  type RadarSourceCandidateApi,
  type RadarTopicApi,
} from '../lib/api';
import { BackLink } from '../components/BackLink';
import { MdLite } from '../components/MdLite';
import { CADENCE_OPTIONS, cadenceForChoice, nextRunLabel, type CadenceEvery } from '../lib/radarCadence';
import { freshnessLine, topicFreshnessLine } from '../lib/radarFreshness';
import { kindChipLabel } from '../lib/radarKind';
import './spread.css';
import './RadarTopics.css'; // shares .rtopics__label/.rtopics__input with the topics window's own form
import './RadarTopic.css';

export interface RadarTopicProps {
  tenantId: string;
  slug: string;
  // R3d (C3) — scopes the item list + the freshness line to one earlier
  // day's own run; undefined is "today" (the unfiltered default).
  runId?: string;
  onBack: () => void;
  onOpenProposal: (id: string) => void;
  // R3d (C3) — opens this same topic filtered to one run (an "Earlier days"
  // row); onBack always climbs to Radar home regardless of which day is
  // showing (parentRoute's own rule for every drilled-in radar screen).
  onOpenRun?: (runId: string) => void;
  // R5 (C) — the "source report" line opens the month's wiki page through
  // the Record window (the same door MdLite's own wikilinks use); optional
  // so a caller with nowhere to send it simply doesn't render the link.
  onOpenPage?: (slug: string) => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | {
      phase: 'ready';
      topic: RadarTopicApi;
      sources: RadarSourceApi[];
      runs: RadarRunApi[];
      items: RadarItemApi[];
      proposals: RadarProposalApi[];
      lastDiscover: RadarLastRunApi | null;
      // R5 (C) — "found, not yet trusted" and the month's source report.
      candidates: RadarSourceCandidateApi[];
      sourceReportSlug: string | null;
    };

const VERDICT_ORDER = ['adapt', 'watch', 'skip'] as const;

function cadenceEveryOf(cadence: Record<string, unknown>): CadenceEvery {
  const every = (cadence as { every?: unknown }).every;
  return every === 'weekly' || every === 'monthly' ? every : 'daily';
}

// R3d (C3) — "Earlier days"' own row label: 'Sat Sep 27 · 06:15 · 18 seen
// → 4 shown'. Built field-by-field (never Date#toLocaleString's own
// combined formatter, which inserts commas this exact spec-quoted format
// does not carry).
function earlierDayLabel(r: RadarRunApi): string {
  if (!r.startedAt) return r.kind ?? 'run';
  const d = new Date(r.startedAt);
  const weekday = d.toLocaleDateString('en-US', { weekday: 'short' });
  const monthDay = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  const hasCounts = r.seen !== null || r.shown !== null;
  return hasCounts ? `${weekday} ${monthDay} · ${time} · ${r.seen ?? 0} seen → ${r.shown ?? 0} shown` : `${weekday} ${monthDay} · ${time}`;
}

export function RadarTopic({ tenantId, slug, runId, onBack, onOpenProposal, onOpenRun, onOpenPage }: RadarTopicProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [consequenceDraft, setConsequenceDraft] = useState('');
  const [cadenceDraft, setCadenceDraft] = useState<CadenceEvery>('daily');
  const [notifyDraft, setNotifyDraft] = useState(false);
  // R3d (C2) — the sources rail: a fold, closed by default.
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [sourceBusy, setSourceBusy] = useState<string | null>(null);
  const [sourceReceipts, setSourceReceipts] = useState<Record<string, string>>({});
  // R5 (C) — the candidates rail: a fold, closed by default, same shape.
  const [candidatesOpen, setCandidatesOpen] = useState(false);
  // R3d (C5) — "Add to build list", per item row.
  const [raiseBusy, setRaiseBusy] = useState<string | null>(null);
  const [raiseReceipts, setRaiseReceipts] = useState<Record<string, string>>({});
  // R3e (C) — "Add to newsletter", per item row.
  const [newsletterBusy, setNewsletterBusy] = useState<string | null>(null);
  const [newsletterReceipts, setNewsletterReceipts] = useState<Record<string, string>>({});

  function refresh() {
    return Promise.all([
      getRadarTopic(tenantId, slug),
      listRadarItems(tenantId, slug, { limit: 200, runId }),
      listRadarProposals(tenantId, { slug }),
      // R5 (C) — "found, not yet trusted": fetched eagerly, same as sources,
      // so the fold's own "Candidates · N" label is real the moment the
      // screen loads rather than only once tapped open.
      listRadarSourceCandidates(tenantId, slug),
    ])
      .then(([t, items, proposals, candidates]) => {
        setLoad({
          phase: 'ready', topic: t.topic, sources: t.sources, runs: t.runs, items, proposals,
          lastDiscover: t.lastDiscover, candidates, sourceReportSlug: t.sourceReportSlug,
        });
        setConsequenceDraft(t.topic.consequenceMd);
        setCadenceDraft(cadenceEveryOf(t.topic.cadence));
        setNotifyDraft(t.topic.notify ?? false);
        void markRadarTopicViewed(tenantId, slug).catch(() => undefined);
      })
      .catch((err: unknown) => {
        setLoad({ phase: 'error', message: err instanceof ApiError ? err.message : 'the radar is not reachable' });
      });
  }

  useEffect(() => {
    setLoad({ phase: 'loading' });
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, slug, runId]);

  async function toggleSource(s: RadarSourceApi) {
    setSourceBusy(s.id);
    try {
      const r = await setRadarSourceState(tenantId, s.id, s.state === 'active' ? 'dormant' : 'active');
      setSourceReceipts((cur) => ({
        ...cur,
        [s.id]: r.receipt ?? (r.source.state === 'dormant' ? `dropped — ${s.name}` : `restored — ${s.name}`),
      }));
      await refresh();
    } catch (err) {
      setSourceReceipts((cur) => ({ ...cur, [s.id]: err instanceof ApiError ? err.message : 'not recorded' }));
    } finally {
      setSourceBusy(null);
    }
  }

  async function addToBuildList(item: RadarItemApi) {
    setRaiseBusy(item.id);
    try {
      const r = await raiseRadarItem(tenantId, item.id);
      setRaiseReceipts((cur) => ({ ...cur, [item.id]: r.receipt ?? `added to Worth building — "${r.proposal?.title ?? item.title ?? item.url}"` }));
    } catch (err) {
      setRaiseReceipts((cur) => ({ ...cur, [item.id]: err instanceof ApiError ? err.message : 'not recorded' }));
    } finally {
      setRaiseBusy(null);
    }
  }

  // R3e (C) — "Add to newsletter", beside "Add to build list"; the tool's
  // own receipt, verbatim.
  async function addToNewsletter(item: RadarItemApi) {
    setNewsletterBusy(item.id);
    try {
      const r = await addToRadarNewsletter(tenantId, item.id);
      setNewsletterReceipts((cur) => ({ ...cur, [item.id]: r.receipt ?? `added to the newsletter — "${item.title ?? item.url}"` }));
    } catch (err) {
      setNewsletterReceipts((cur) => ({ ...cur, [item.id]: err instanceof ApiError ? err.message : 'not recorded' }));
    } finally {
      setNewsletterBusy(null);
    }
  }

  async function researchNow() {
    setBusy(true);
    setNote(null);
    try {
      const cmd = await researchRadarTopicNow(tenantId, slug);
      setNote(`researching · ${cmd.receipt ?? cmd.status}`);
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'not recorded');
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit() {
    if (load.phase !== 'ready') return;
    setBusy(true);
    setNote(null);
    try {
      const { topic, receipt } = await updateRadarTopic(tenantId, slug, {
        name: load.topic.name,
        consequenceMd: consequenceDraft,
        queries: load.topic.queries,
        exclusions: load.topic.exclusions,
        geo: load.topic.geo ?? undefined,
        languages: load.topic.languages ?? undefined,
        state: load.topic.state as 'tracking' | 'paused' | 'once' | 'archived',
        cadence: cadenceForChoice(cadenceDraft),
        notify: notifyDraft,
      });
      setEditing(false);
      // R3b-3 (P4/C1): the tool's own receipt, verbatim — the client-composed
      // fallback only covers a Pythia build that predates P4 (writeTopic
      // answers receipt:null then).
      setNote(receipt ?? `saved — ${nextRunLabel(topic.nextDueAt)}`);
      await refresh();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'not saved');
    } finally {
      setBusy(false);
    }
  }

  if (load.phase === 'loading') {
    return (
      <div class="sp">
        <BackLink label="Radar" onClick={onBack} />
        <div class="sp__say">One moment…</div>
      </div>
    );
  }
  if (load.phase === 'error') {
    return (
      <div class="sp">
        <BackLink label="Radar" onClick={onBack} />
        <div class="sp__say">
          The radar is not reachable.
          <br />
          <span class="sp__say-soft">{load.message}</span>
        </div>
      </div>
    );
  }

  const { topic, sources, runs, items, proposals, lastDiscover, candidates, sourceReportSlug } = load;
  const itemToProposal = new Map<string, { index: number; id: string }>();
  proposals.forEach((p, i) => p.fromItemIds.forEach((itemId) => itemToProposal.set(itemId, { index: i + 1, id: p.id })));

  return (
    <div class="sp">
      <BackLink label="Radar" onClick={onBack} />

      <header class="sp__hero-top">
        <div>
          <div class="sp__label">
            <span class="sp__chip sp__chip--accent">topic · {topic.state}</span>
          </div>
          <h1 class="sp__title">{topic.name}</h1>
          {!editing ? (
            <p class="sp__sub">{topic.consequenceMd}</p>
          ) : (
            <>
              <textarea
                class="rtopic__edit-consequence"
                rows={3}
                value={consequenceDraft}
                onInput={(e) => setConsequenceDraft((e.target as HTMLTextAreaElement).value)}
              />
              <div class="rtopic__edit-cadence">
                <label class="rtopics__label" for="rtopic-cadence">cadence</label>
                <select
                  id="rtopic-cadence"
                  class="rtopics__input"
                  value={cadenceDraft}
                  onChange={(e) => setCadenceDraft((e.target as HTMLSelectElement).value as CadenceEvery)}
                >
                  {CADENCE_OPTIONS.map((c) => (
                    <option value={c.id} key={c.id}>{c.label}</option>
                  ))}
                </select>
                <label class="rtopic__edit-notify">
                  <input
                    type="checkbox"
                    checked={notifyDraft}
                    onChange={(e) => setNotifyDraft((e.target as HTMLInputElement).checked)}
                  />
                  email when this topic reviews
                </label>
              </div>
            </>
          )}
        </div>
      </header>

      <div class="rtopic__acts">
        <button type="button" class="sp__act" disabled={busy} onClick={() => void researchNow()}>
          Research now
        </button>
        {!editing ? (
          <button type="button" class="sp__act" onClick={() => setEditing(true)}>Edit topic</button>
        ) : (
          <>
            <button type="button" class="sp__act" disabled={busy} onClick={() => void saveEdit()}>Save</button>
            <button
              type="button"
              class="sp__act"
              disabled={busy}
              onClick={() => {
                setEditing(false);
                setConsequenceDraft(topic.consequenceMd);
                setCadenceDraft(cadenceEveryOf(topic.cadence));
                setNotifyDraft(topic.notify ?? false);
              }}
            >
              Never mind
            </button>
          </>
        )}
      </div>
      {note ? <p class="sp__age">{note}</p> : null}
      <p class="rtopic__freshness">
        {topicFreshnessLine(lastDiscover, runs[0] ?? null)}
      </p>

      <div class="sp__grid">
        <div class="sp__hero">
          {VERDICT_ORDER.filter((v) => items.some((i) => i.verdict === v)).map((verdict) => (
            <div key={verdict}>
              <div class="sp__sec"><span>{verdict}</span></div>
              {items
                .filter((i) => i.verdict === verdict)
                .map((i) => (
                  <div class="sp__row" key={i.id} style={{ cursor: 'default' }}>
                    <span class="sp__row-top">
                      {/* R3d (C1) — the kind chip, a WORD before the title, never an icon alone. */}
                      <span class="sp__chip">{kindChipLabel(i.kind, i.durationSeconds)}</span>
                      <a class="rtopic__item-title" href={i.url} target="_blank" rel="noreferrer">
                        {i.title ?? i.url}
                      </a>
                      <span class="sp__row-when">{new Date(i.firstSeenAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>
                    </span>
                    <div class="rtopic__row-acts">
                      {itemToProposal.has(i.id) ? (
                        <button type="button" class="rtopic__link" onClick={() => onOpenProposal(itemToProposal.get(i.id)!.id)}>
                          → proposal {itemToProposal.get(i.id)!.index}
                        </button>
                      ) : null}
                      {/* R3d (C5) — "Add to build list", on any item, any verdict. A
                          real button in the row's own action strip (tunnel walk
                          feedback: it used to run into the proposal link with no
                          gap and read as plain text). */}
                      {raiseReceipts[i.id] ? (
                        <span class="rtopic__raise-receipt">{raiseReceipts[i.id]}</span>
                      ) : (
                        <button
                          type="button"
                          class="rtopic__link rtopic__link--action"
                          disabled={raiseBusy === i.id}
                          onClick={() => void addToBuildList(i)}
                        >
                          Add to build list
                        </button>
                      )}
                      {/* R3e (C) — "Add to newsletter", the same shape. */}
                      {newsletterReceipts[i.id] ? (
                        <span class="rtopic__raise-receipt">{newsletterReceipts[i.id]}</span>
                      ) : (
                        <button
                          type="button"
                          class="rtopic__link rtopic__link--action"
                          disabled={newsletterBusy === i.id}
                          onClick={() => void addToNewsletter(i)}
                        >
                          Add to newsletter
                        </button>
                      )}
                    </div>
                    {/* R3e (C, tunnel walk feedback): rendered through the same
                        markdown renderer as a proposal's own body, and with the
                        reading's own duplicate heading (its "n. title" line,
                        already shown as this row's title) dropped. */}
                    {i.readingMd ? <MdLite md={i.readingMd} dedupeTitle={i.title} /> : null}
                  </div>
                ))}
            </div>
          ))}
          {items.length === 0 ? <div class="sp__empty">No items yet.</div> : null}
        </div>

        <div class="sp__aside">
          {/* R3d (C2) — the sources rail earns its place or folds: closed
              by default, opens in place (no navigation, no new screen). */}
          <button type="button" class="rtopic__fold-toggle" onClick={() => setSourcesOpen((o) => !o)}>
            <span class="sp__sec"><span>Sources · {sources.length}</span></span>
          </button>
          {sourcesOpen ? (
            sources.length === 0 ? (
              <div class="sp__empty">No sources yet.</div>
            ) : (
              sources.map((s) => (
                <div class="sp__row" key={s.id} style={{ cursor: 'default' }}>
                  <span class="sp__row-top">
                    {s.url ? (
                      <a class="rtopic__item-title" href={s.url} target="_blank" rel="noreferrer">{s.name}</a>
                    ) : (
                      <span class="sp__row-name">{s.name}</span>
                    )}
                    {/* R5 (C) — probation is its own chip, a word not a
                        colour (design rule 4: "the cockpit's sources rail
                        shows 'probation' as its own state"). */}
                    <span class="sp__chip">{s.state}</span>
                  </span>
                  {/* R5 (C) — a probation row's own reason, as its sub-line. */}
                  {s.state === 'probation' ? <span class="sp__row-sub">{s.addedBecause}</span> : null}
                  <span class="sp__row-sub">{s.items30d} item{s.items30d === 1 ? '' : 's'} in 30 days</span>
                  <span class="sp__row-sub">last: {s.lastItemUrl ? (
                    <a class="rtopic__link" href={s.lastItemUrl} target="_blank" rel="noreferrer">{s.lastItemTitle ?? s.lastItemUrl}</a>
                  ) : (s.lastItemTitle ?? 'nothing yet')}</span>
                  {sourceReceipts[s.id] ? (
                    <span class="rtopic__raise-receipt">{sourceReceipts[s.id]}</span>
                  ) : (
                    <button
                      type="button"
                      class="rtopic__link"
                      disabled={sourceBusy === s.id}
                      onClick={() => void toggleSource(s)}
                    >
                      {s.state === 'active' ? 'Drop' : 'Restore'}
                    </button>
                  )}
                </div>
              ))
            )
          ) : null}

          {/* R5 (C) — "found, not yet trusted": read-only, machine voice,
              no captions. Folded closed by default, same shape as Sources. */}
          <button type="button" class="rtopic__fold-toggle" onClick={() => setCandidatesOpen((o) => !o)}>
            <span class="sp__sec"><span>Candidates · {candidates.length}</span></span>
          </button>
          {candidatesOpen ? (
            candidates.length === 0 ? (
              <div class="sp__empty">No candidates yet.</div>
            ) : (
              candidates.map((c) => (
                <div class="sp__row" key={c.id} style={{ cursor: 'default' }}>
                  <span class="sp__row-top">
                    <span class="sp__row-name">{c.name ?? c.kind}</span>
                    <span class="sp__chip">{c.state}</span>
                  </span>
                  <span class="sp__row-sub">
                    {c.citations} citation{c.citations === 1 ? '' : 's'} · {c.citingGroups.slice(0, 2).join(', ')}
                    {c.citingGroups.length > 2 ? ` +${c.citingGroups.length - 2}` : ''}
                  </span>
                </div>
              ))
            )
          ) : null}

          {/* R5 (C) — the month's source report, when one has been written. */}
          {sourceReportSlug ? (
            <div class="sp__row-sub">
              {onOpenPage ? (
                <a
                  class="rtopic__link"
                  href={`/record/page/${sourceReportSlug}`}
                  onClick={(e) => {
                    e.preventDefault();
                    onOpenPage(sourceReportSlug);
                  }}
                >
                  source report
                </a>
              ) : (
                <span>source report: {sourceReportSlug}</span>
              )}
            </div>
          ) : null}

          {/* R3d (C3) — "Earlier days" open: each row filters this same
              screen to that run; today (no runId) is the default. */}
          <div class="sp__sec" style={{ marginTop: '16px' }}>
            <span>Earlier days{runId ? ' — filtered' : ''}</span>
          </div>
          {runs.length === 0 ? (
            <div class="sp__empty">No earlier runs.</div>
          ) : (
            runs.map((r) => (
              <button
                type="button"
                class="rtopic__run-row"
                key={r.id}
                disabled={!onOpenRun}
                onClick={() => onOpenRun?.(r.id)}
              >
                <span class="sp__row-top">
                  <span class="sp__row-name">{earlierDayLabel(r)}</span>
                  {r.id === runId ? <span class="sp__chip sp__chip--accent">showing</span> : null}
                </span>
                {r.pageSlug ? <span class="sp__row-sub">brief: {r.pageSlug}</span> : null}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
